import { useState, type FormEvent, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import EditRounded from "@mui/icons-material/EditRounded";
import MoreHorizRounded from "@mui/icons-material/MoreHorizRounded";
import EventRounded from "@mui/icons-material/EventRounded";
import ForumRounded from "@mui/icons-material/ForumRounded";
import { Link as RouterLink, useLocation, useParams, useSearchParams } from "react-router-dom";
import { useAuth, useFeed } from "@bw/shared/hooks";
import { Page } from "../../../common/components/Page";
import { useCommunity } from "../hooks/useCommunity";
import { useCommunityMembership } from "../hooks/useCommunityMembership";
import { useCommunityAdmissions } from "../hooks/useCommunityAdmissions";
import { useCreatePost } from "../hooks/useCreatePost";
import { MaterialSocialPostCard } from "../../../common/components/MaterialSocialPostCard";
import { PostActions } from "../components/PostActions";
import { PostMenu } from "../components/PostMenu";
import { FeedSkeleton } from "../components/FeedSkeleton";
import { CommunityChannels } from "../components/CommunityChannels";
import { CommunityGameNights } from "../components/CommunityGameNights";
import { readSearchReturn } from "../../../common/searchNavigation";
import { CommunityAvatar } from "../components/CommunityAvatar";
import { SectionHeader } from "../../../common/components/SectionHeader";
import { GuardianAwareError } from "../../../common/guardian/GuardianAwareError";
import { INLINE_GUARDIAN_PROMPT } from "../../../common/guardian/denials";
import { guardianDenialFromError } from "@bw/shared/api";

/**
 * A community's detail surface (/community/communities/:communityId). useCommunity
 * loads the community → name / description / member_count / meta (visibility +
 * join_mode, human-readable). The SERVER's `is_member` flag drives the CTA: a
 * member sees Leave; an open community sees Join; request mode creates a
 * persisted request; invite-only mode clearly explains that an invitation is
 * required and never calls the direct-join endpoint.
 *
 * Join / Leave call communityApi and, on success, INVALIDATE — the membership
 * query refetches and the page reflects the SERVER's truth (the CTA flips, the
 * member_count is the refetched value). There is NO optimistic update: the count
 * is never bumped client-side, and a failed join (403 / 409 / network) surfaces a
 * role="alert" with the Join button left in place (no phantom flip to Leave).
 * The button is disabled while the mutation is pending so a double-click fires
 * once. A failed load (404 / reject) is an honest error with a back-link to the
 * hub. The raw community_id is never rendered as visible text.
 *
 * Slice 3 (WRITE) adds a COMPOSE box to the feed region, rendered ONLY when the
 * server's `is_member` is true (a non-member sees no box — the backend would 403,
 * so we don't show a disabled box that 403s). Content is required + trimmed (an
 * empty / whitespace body blocks submit — useCreatePost is never called). Submit
 * → useCreatePost → merge the confirmed server response into the canonical Feed,
 * then run bounded projection reconciliation. A failed create surfaces
 * a role="alert" with the typed content RETAINED (the user retries without
 * re-typing) and adds no phantom card. Submit is disabled while pending so a
 * double-click fires once.
 *
 * The AppShell supplies no page title; this routed page OWNS its single <h1> via
 * <Page title={…}> (WCAG 1.3.1 / 2.4.6 — exactly one h1 per page).
 */

/** Map a backend enum-ish string to a human-readable label (e.g. join_mode
 *  "request_to_join" → "Request to join"). Falls back to the raw token if empty. */
function humanize(value: string): string {
  const trimmed = value?.trim();
  if (!trimmed) return value;
  const spaced = trimmed.replace(/[_-]+/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Saved ?view=feed links from the retired Posts view land on Home with posts.
type CommunityView = "home" | "chat" | "events";
const COMMUNITY_VIEWS = new Set<CommunityView>(["home", "chat", "events"]);

export function CommunityDetailPage() {
  const { user, loading } = useAuth();
  const { communityId } = useParams<{ communityId: string }>();
  if (loading || !user) {
    return <Page title="Community"><Typography role="status">
      {loading ? "Loading community…" : "Sign in to view this community."}
    </Typography></Page>;
  }
  return <ScopedCommunityDetailPage key={`${user.uid}:${communityId}`} />;
}

function ScopedCommunityDetailPage() {
  const { communityId } = useParams<{ communityId: string }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get("view") as CommunityView | null;
  const view = requestedView && COMMUNITY_VIEWS.has(requestedView)
    ? requestedView
    : "home";
  const searchReturn = readSearchReturn(location.search);
  const communityQ = useCommunity(communityId);
  const { join, leave, request } = useCommunityMembership(communityId ?? "", INLINE_GUARDIAN_PROMPT);
  const admissionsQ = useCommunityAdmissions({
    enabled: communityQ.data?.join_mode === "invite_only",
  });
  // The post feed is a SEPARATE query (its own cache slot) so a feed failure is
  // independent of the community header: a Feed reject surfaces a
  // role="alert" in the feed region only — the name / meta / CTA above still
  // render. The page is NEVER gated on the feed.
  const feedQ = useFeed(
    { scope: "community", communityId: communityId ?? "" },
    {
      enabled: Boolean(communityId) && view === "home",
      freshness: {
        enabled: Boolean(communityId) && view === "home",
        intervalMs: 30_000,
      },
    },
  );

  // ---- load states --------------------------------------------------------
  if (communityQ.isLoading) {
    return (
      <Page title="Community">
        <Typography color="text.secondary" role="status">
          Loading community…
        </Typography>
      </Page>
    );
  }

  if (communityQ.isError || !communityQ.data) {
    return (
      <Page title="Community">
        <Alert
          severity="error"
          role="alert"
          action={
            <Button
              component={RouterLink}
              to={searchReturn ?? "/community"}
              color="inherit"
              size="small"
            >
              {searchReturn ? "Back to search" : "Back to communities"}
            </Button>
          }
        >
          We couldn&apos;t load this community. It may not exist.
        </Alert>
      </Page>
    );
  }

  const community = communityQ.data;
  // The most recent membership error across the two mutations — surfaced honestly
  // without flipping the CTA (no optimistic state). It DEFERS to a feed-load
  // failure: when the feed itself is broken, the feed region already owns the
  // page's single assertive role="alert" (the bigger problem), so a transient
  // membership-action error is announced only while the feed is healthy. This
  // keeps exactly one assertive alert on the page and avoids two competing
  // role="alert" live regions fighting for the screen-reader's attention.
  //
  // A guardian refusal (#2691) is the exception: these mutations show it
  // inline only (not app-wide), so it must still appear when the feed has
  // failed for another reason, or the child would never learn why.
  const latestActionError =
    request?.error ??
    join.error ??
    leave.error ??
    admissionsQ.accept.error ??
    admissionsQ.decline.error;
  // (When the feed failure is itself a guardian answer, the feed region
  // already shows it.)
  const actionError = !feedQ.isError
    ? latestActionError
    : guardianDenialFromError(latestActionError) && !guardianDenialFromError(feedQ.error)
      ? latestActionError
      : null;
  const invitation = admissionsQ.admissions.data?.items.find(
    (admission) =>
      admission.community_id === community.community_id &&
      admission.kind === "invitation" &&
      admission.status === "pending",
  );
  const isMember = community.is_member;
  const changeView = (next: CommunityView) => {
    const params = new URLSearchParams(searchParams);
    if (next === "home") params.delete("view");
    else params.set("view", next);
    setSearchParams(params);
  };
  const focusComposer = () => {
    const field = document.getElementById("compose-post");
    field?.scrollIntoView({ behavior: "smooth", block: "center" });
    field?.focus({ preventScroll: true });
  };
  const homeParams = new URLSearchParams(searchParams);
  homeParams.delete("view");
  const backTo =
    view === "home"
      ? (searchReturn ?? "/community")
      : `${location.pathname}${homeParams.size > 0 ? `?${homeParams}` : ""}`;
  const membershipAction = isMember ? null : community.join_mode === "request" ? (
    <Button size="small" variant="contained" disabled={request?.isPending} onClick={() => request?.mutate()}>
      Request to join
    </Button>
  ) : community.join_mode === "invite_only" ? (
    <Button size="small" variant="outlined" disabled>
      Invitation required
    </Button>
  ) : (
    <Button size="small" variant="contained" disabled={join.isPending} onClick={() => join.mutate()}>
      Join
    </Button>
  );

  return (
    <Stack spacing={2} sx={{ width: "100%", minWidth: 0, maxWidth: 720 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <IconButton
          component={RouterLink}
          to={backTo}
          aria-label={view === "home" ? (searchReturn ? "Back to search" : "Back to communities") : "Back to community"}
          edge="start"
        >
          <ArrowBackRounded />
        </IconButton>
        <CommunityAvatar communityId={community.community_id} name={community.name} size={40} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              fontSize: "1.125rem",
              fontWeight: 700,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              overflowWrap: "anywhere",
            }}
          >
            {community.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {[
              humanize(community.visibility),
              humanize(community.join_mode),
              typeof community.member_count === "number"
                ? `${community.member_count} ${community.member_count === 1 ? "member" : "members"}`
                : "— members",
            ].join(" · ")}
          </Typography>
        </Box>
        {membershipAction}
        <CommunityOptionsMenu
          name={community.name}
          isMember={isMember}
          leaving={leave.isPending}
          onLeave={() => leave.mutate()}
        />
      </Stack>

      {community.description && view === "home" ? (
        <CollapsibleText text={community.description} />
      ) : null}

      {actionError ? (
        <GuardianAwareError error={actionError}>
          <Alert severity="error" role="alert">
            That didn&apos;t work. Please try again.
          </Alert>
        </GuardianAwareError>
      ) : null}

      {invitation ? (
        <Alert severity="info">
          You have a pending invitation to this community.
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="contained"
              disabled={admissionsQ.accept.isPending || admissionsQ.decline.isPending}
              onClick={() => admissionsQ.accept.mutate(invitation.admission_id)}
            >
              Accept invitation
            </Button>
            <Button
              size="small"
              disabled={admissionsQ.accept.isPending || admissionsQ.decline.isPending}
              onClick={() => admissionsQ.decline.mutate(invitation.admission_id)}
            >
              Decline
            </Button>
          </Stack>
        </Alert>
      ) : null}

      {view === "chat" ? (
        isMember ? (
          <CommunityChannels communityId={communityId ?? ""} />
        ) : (
          <Alert severity="info" role="status">Join this community to open member chats.</Alert>
        )
      ) : view === "events" ? (
        isMember ? (
          <CommunityGameNights communityId={communityId ?? ""} ownerFacilityId={community.owner_facility_id} />
        ) : (
          <Alert severity="info" role="status">Join this community to see and sign up for events.</Alert>
        )
      ) : (
        <>
          {isMember ? (
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }} role="group" aria-label="Quick actions">
              <Chip size="small" icon={<ForumRounded />} label="Chat" onClick={() => changeView("chat")} />
              <Chip size="small" icon={<EventRounded />} label="Events" onClick={() => changeView("events")} />
              {community.can_post ? (
                <Chip size="small" icon={<EditRounded />} label="New post" onClick={focusComposer} />
              ) : null}
            </Stack>
          ) : (
            <Alert severity="info" role="status">Join this community to see its chats and events.</Alert>
          )}
          {isMember ? (
            <>
              <CommunityGameNights
                communityId={communityId ?? ""}
                ownerFacilityId={community.owner_facility_id}
                compact
              />
              <CommunityChannels communityId={communityId ?? ""} compact />
            </>
          ) : null}
          <Stack component="section" spacing={1.5} aria-labelledby="community-posts">
            <SectionHeader id="community-posts" title="Posts" />
            {community.system_read_only ? (
              <Alert severity="info" role="status">
                Learner posting is read-only. Class history and resources remain available; authorized staff can still post.
              </Alert>
            ) : null}
            {community.can_post === true ? (
              <ComposePost communityId={communityId ?? ""} communityName={community.name} />
            ) : null}
            <CommunityFeed feedQ={feedQ} />
          </Stack>
        </>
      )}
    </Stack>
  );
}

/** Secondary community actions live behind ⋯ so the header stays one row. */
function CommunityOptionsMenu({
  name,
  isMember,
  leaving,
  onLeave,
}: {
  name: string;
  isMember: boolean;
  leaving: boolean;
  onLeave: () => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState<"copied" | "failed" | null>(null);
  const close = () => setAnchor(null);
  const copyLink = () => {
    close();
    const link = `${window.location.origin}${window.location.pathname}`;
    if (!navigator.clipboard) {
      setCopied("failed");
      return;
    }
    navigator.clipboard.writeText(link).then(
      () => setCopied("copied"),
      () => setCopied("failed"),
    );
  };
  return (
    <>
      <IconButton
        aria-label="Community options"
        aria-haspopup="menu"
        aria-expanded={anchor ? "true" : undefined}
        aria-controls={anchor ? "community-options-menu" : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        <MoreHorizRounded />
      </IconButton>
      <Menu id="community-options-menu" anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        <MenuItem onClick={copyLink}>Copy link</MenuItem>
        {isMember ? (
          <MenuItem
            onClick={() => {
              close();
              setConfirming(true);
            }}
            sx={{ color: "error.main" }}
          >
            Leave community
          </MenuItem>
        ) : null}
      </Menu>
      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>Leave {name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You&apos;ll stop seeing its chats and events. Your posts stay visible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Stay</Button>
          <Button
            color="error"
            disabled={leaving}
            onClick={() => {
              setConfirming(false);
              onLeave();
            }}
          >
            Leave
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={copied !== null}
        autoHideDuration={2500}
        onClose={() => setCopied(null)}
        message={copied === "failed" ? "Couldn't copy the link" : "Link copied"}
      />
    </>
  );
}

/** Two-line description with an inline "more" toggle. */
function CollapsibleText({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 140;
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      sx={
        open || !long
          ? undefined
          : { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }
      }
    >
      {text}
      {long ? (
        <Button size="small" onClick={() => setOpen((value) => !value)} sx={{ ml: 0.5, minWidth: 0, p: 0, verticalAlign: "baseline" }}>
          {open ? "less" : "more"}
        </Button>
      ) : null}
    </Typography>
  );
}

/**
 * The compose-a-post box (Slice 3, members only). A controlled textarea + Submit.
 * Content is required + trimmed: an empty / whitespace body blocks submit, so
 * useCreatePost is never called. On submit the TRIMMED content is sent; on
 * success the textarea clears and the exact server response joins the loaded Feed;
 * bounded reconciliation catches asynchronous projection changes. On failure
 * (403 / network) a role="alert" surfaces and the typed content is RETAINED so
 * the user can retry without re-typing. Submit is disabled while pending so a
 * double-click fires the mutation once.
 */
function ComposePost({ communityId, communityName }: { communityId: string; communityName: string }) {
  const [content, setContent] = useState("");
  const createPost = useCreatePost(communityId);

  const trimmed = content.trim();
  const canSubmit = trimmed.length > 0 && !createPost.isPending;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Content guard — never call the API with an empty / whitespace body.
    if (!trimmed || createPost.isPending) return;
    createPost.mutate(trimmed, {
      // Clear ONLY on confirmed success — a failure keeps the body for a retry.
      onSuccess: () => setContent(""),
    });
  };

  return (
    <Stack component="form" spacing={1} onSubmit={handleSubmit}>
      {createPost.isError ? (
        <GuardianAwareError error={createPost.error}>
          <Alert severity="error" role="alert">
            We couldn&apos;t post that. Please try again.
          </Alert>
        </GuardianAwareError>
      ) : null}
      <Stack direction="row" spacing={1} sx={{ alignItems: "flex-end" }}>
        <TextField
          id="compose-post"
          value={content}
          onChange={(event) => setContent(event.target.value)}
          placeholder={`Share something with ${communityName}…`}
          slotProps={{ htmlInput: { "aria-label": "Share with the community" } }}
          size="small"
          minRows={1}
          maxRows={8}
          multiline
          fullWidth
        />
        <Button type="submit" variant="contained" disabled={!canSubmit}>
          Post
        </Button>
      </Stack>
    </Stack>
  );
}

/**
 * The community's post feed region — driven by its OWN query so its states are
 * independent of the header. Loading → role="status"; error → role="alert" (the
 * header above is untouched); empty → honest "No posts yet"; otherwise one
 * PostCard per server post — each now carrying the shared PostActions bar (Like ·
 * Comment · Repost), the SAME affordance as the friends feed, so a community post
 * is a first-class interactive post, not a read-only row. The compose box (Slice 3)
 * lives above this region, rendered only for members.
 */
function CommunityFeed({
  feedQ,
  compact = false,
}: {
  feedQ: ReturnType<typeof useFeed>;
  compact?: boolean;
}) {
  const fetchNextPage = feedQ.fetchNextPage;
  if (feedQ.isLoading) {
    return <FeedSkeleton />;
  }

  if (feedQ.isError && !feedQ.isFetchNextPageError) {
    // ANY feed-load failure — a server 4xx/5xx OR an unreachable-backend
    // transport fault — is announced ASSERTIVELY (role="alert"). A real "couldn't
    // load" error deserves the strong screen-reader signal regardless of the
    // rejection's type; it must never be downgraded to a polite status. The
    // failure is contained to THIS region — the community header is untouched.
    // A look-only minor (#2691) is told why posts wait, not "couldn't load".
    return (
      <GuardianAwareError error={feedQ.error}>
        <Alert
          severity="error"
          role="alert"
          action={
            <Button color="inherit" size="small" onClick={() => feedQ.refetch()}>
              Try again
            </Button>
          }
        >
          We couldn&apos;t load the posts.
        </Alert>
      </GuardianAwareError>
    );
  }

  const posts = feedQ.posts;
  if (posts.length === 0 && !feedQ.hasNextPage) {
    return <Typography color="text.secondary">No posts yet.</Typography>;
  }

  return (
    <Stack spacing={2}>
      {!compact && feedQ.freshnessError ? (
        <Alert
          severity="warning"
          role="status"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={feedQ.isCheckingFreshness}
              onClick={() => void feedQ.checkFreshness()}
            >
              Try again
            </Button>
          }
        >
          Post updates are temporarily unavailable; loaded posts remain.
        </Alert>
      ) : !compact && feedQ.newPostsAvailable ? (
        <Alert
          severity="info"
          role="status"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={feedQ.isApplyingFreshness || feedQ.isCheckingFreshness}
              onClick={() => void feedQ.refreshNewPosts()}
            >
              {feedQ.isApplyingFreshness ? "Updating…" : "Show new posts"}
            </Button>
          }
        >
          New community posts are available.
        </Alert>
      ) : null}
      <List disablePadding sx={{ display: "grid", gap: 2 }}>
        {(compact ? posts.slice(0, 2) : posts).map((post) => (
          <ListItem key={post.post_id} disablePadding sx={{ display: "block" }}>
            <MaterialSocialPostCard
              post={post}
              actions={<PostActions post={post} />}
              menu={<PostMenu post={post} />}
              detailPath={(id) => `/community/posts/${id}`}
              profilePath={(uid) => `/players/${uid}`}
            />
          </ListItem>
        ))}
      </List>
      {!compact && feedQ.isFetchNextPageError ? (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => fetchNextPage()}
            >
              Retry
            </Button>
          }
        >
          We couldn&apos;t load more posts.
        </Alert>
      ) : null}
      {!compact && feedQ.hasNextPage && !feedQ.isFetchNextPageError ? (
        <Button
          type="button"
          variant="outlined"
          disabled={feedQ.isFetchingNextPage || feedQ.isApplyingFreshness}
          onClick={() => fetchNextPage()}
          sx={{ alignSelf: "center" }}
        >
          {feedQ.isFetchingNextPage ? (
            <span
              role="status"
              aria-live="polite"
              aria-label="Loading more posts"
            >
              Loading more…
            </span>
          ) : (
            "Load more"
          )}
        </Button>
      ) : !compact && !feedQ.hasNextPage ? (
        <Typography color="text.secondary" sx={{ textAlign: "center" }}>
          You&apos;re all caught up.
        </Typography>
      ) : null}
    </Stack>
  );
}
